import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { applySeo } from "../lib/seo";
import {
  FUWALO_LOGO_ABSOLUTE_URL,
  FUWALO_LOGO_PATH,
  FUWALO_OG_SCREENSHOT_URL,
} from "../constants/branding";
import { PLAY_STORE_URL, DISCORD_URL, TWITTER_URL } from "../constants/links";

const FUWALO_SITE = "https://fuwalo.com";

/* ------------------------------------------------------------------ */
/*  Fuwalo Production Home Page                                       */
/*  Clean, modern, high-converting, and professional landing page.    */
/* ------------------------------------------------------------------ */

export const FuwaloHome: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    applySeo({
      title: "Fuwalo — Multiplayer Piano App, MIDI Practice & Piano Learning",
      description:
        "Play piano with friends in real time, practice with MIDI songs, connect your USB MIDI keyboard, and master interactive lessons. Download Fuwalo for free on Google Play.",
      canonicalPath: "/",
      ogImageUrl: FUWALO_OG_SCREENSHOT_URL,
      ogType: "website",
      jsonLd: {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Organization",
            "@id": `${FUWALO_SITE}/#organization`,
            name: "Fuwalo",
            url: `${FUWALO_SITE}/`,
            logo: { "@type": "ImageObject", url: FUWALO_LOGO_ABSOLUTE_URL },
            sameAs: [TWITTER_URL, DISCORD_URL, PLAY_STORE_URL],
          },
          {
            "@type": "WebSite",
            "@id": `${FUWALO_SITE}/#website`,
            url: `${FUWALO_SITE}/`,
            name: "Fuwalo",
            publisher: { "@id": `${FUWALO_SITE}/#organization` },
            inLanguage: "en-US",
          },
          {
            "@type": "SoftwareApplication",
            name: "Fuwalo Piano",
            applicationCategory: "MusicApplication",
            operatingSystem: "Android",
            downloadUrl: PLAY_STORE_URL,
            offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
            description:
              "Multiplayer piano app with MIDI playback, virtual keyboard, rhythm-game modes, sight-reading practice, USB MIDI, and online duets.",
            image: FUWALO_OG_SCREENSHOT_URL,
          },
        ],
      },
    });
  }, []);

  /* Scroll progress bar -------------------------------------------- */
  useEffect(() => {
    const onScroll = () => {
      const el = progressRef.current;
      if (!el) return;
      const scrollTop = window.scrollY;
      const docHeight = document.body.offsetHeight - window.innerHeight;
      el.style.width = `${docHeight > 0 ? (scrollTop / docHeight) * 100 : 0}%`;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Scroll-in observer --------------------------------------------- */
  useEffect(() => {
    const els = document.querySelectorAll<HTMLElement>(".fw-scroll-animate");
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("fw-animate");
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <style>{fuwaloStyles}</style>

      {/* Scroll progress bar */}
      <div ref={progressRef} className="fw-scroll-progress" />

      {/* Navigation */}
      <nav className="fw-navbar">
        <div className="fw-nav-inner">
          <Link to="/" className="fw-nav-brand">
            <img
              src="/favicon.svg"
              alt="Fuwalo Logo"
              width={34}
              height={34}
              style={{ marginRight: 10, objectFit: "contain" }}
            />
            <span className="fw-brand-text">Fuwalo</span>
          </Link>

          <div className="fw-nav-links-desktop">
            <a href="#features" className="fw-nav-link">Features</a>
            <a href="#showcase" className="fw-nav-link">App Preview</a>
            <a href="#download" className="fw-nav-link">Download</a>
            <a href="#faq" className="fw-nav-link">FAQ</a>
            <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" className="fw-nav-link" aria-label="Discord Community">
              <i className="fab fa-discord" />
            </a>
          </div>

          <div className="fw-nav-action-desktop">
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="fw-btn fw-btn-nav"
            >
              <i className="fab fa-google-play" style={{ marginRight: 8 }} />
              Get on Google Play
            </a>
          </div>

          <button
            className="fw-mobile-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle navigation menu"
          >
            <i className={`fas ${mobileMenuOpen ? "fa-times" : "fa-bars"}`} />
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="fw-mobile-menu">
            <a href="#features" onClick={() => setMobileMenuOpen(false)}>Features</a>
            <a href="#showcase" onClick={() => setMobileMenuOpen(false)}>App Preview</a>
            <a href="#download" onClick={() => setMobileMenuOpen(false)}>Download</a>
            <a href="#faq" onClick={() => setMobileMenuOpen(false)}>FAQ</a>
            <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)}>
              <i className="fab fa-discord" style={{ marginRight: 8, color: "#5865F2" }} />
              Discord Community
            </a>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="fw-btn fw-btn-playstore fw-mobile-play-btn"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="fab fa-google-play" style={{ marginRight: 8 }} />
              Download on Google Play
            </a>
          </div>
        )}
      </nav>

      {/* ========== HERO ========== */}
      <section className="fw-hero">
        <video className="fw-hero-video" autoPlay muted loop playsInline aria-hidden="true">
          <source src="/fuwalo-hero-video.mp4" type="video/mp4" />
        </video>
        <div className="fw-hero-overlay" aria-hidden="true" />

        {/* Floating notes background decoration */}
        <div className="fw-floating-note" style={{ left: "10%", animationDelay: "0s", fontSize: "1rem" }}><i className="fas fa-music" /></div>
        <div className="fw-floating-note" style={{ left: "85%", animationDelay: "5s", fontSize: "1.2rem" }}><i className="fas fa-music" /></div>
        <div className="fw-floating-note" style={{ left: "50%", animationDelay: "10s", fontSize: "0.9rem" }}><i className="fas fa-music" /></div>

        <div className="fw-hero-content">
          <div className="fw-hero-grid">
            <div className="fw-hero-text fw-scroll-animate">
              <div className="fw-hero-badge">
                <span className="fw-pulse-dot" />
                <span>Now Available on Google Play</span>
              </div>

              <h1 className="fw-hero-title">
                Play Piano With Friends.<br />
                <span className="fw-hero-title-accent">Anytime, Anywhere.</span>
              </h1>

              <p className="fw-hero-subtitle">
                The real-time multiplayer piano and learning app for Android. Connect your USB MIDI keyboard, jam in live rooms, master rhythm games, and practice your favorite songs solo or together.
              </p>

              {/* Highlights */}
              <div className="fw-hero-highlights">
                <div className="fw-highlight-item">
                  <i className="fas fa-check-circle" />
                  <span>Real-Time Multiplayer</span>
                </div>
                <div className="fw-highlight-item">
                  <i className="fas fa-check-circle" />
                  <span>USB MIDI Support</span>
                </div>
                <div className="fw-highlight-item">
                  <i className="fas fa-check-circle" />
                  <span>Free on Google Play</span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="fw-hero-actions">
                <a
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fw-google-play-badge"
                  aria-label="Download Fuwalo on Google Play"
                >
                  <i className="fab fa-google-play fw-play-icon" />
                  <div className="fw-play-text">
                    <span className="fw-play-sub">GET IT ON</span>
                    <span className="fw-play-main">Google Play</span>
                  </div>
                </a>

                <a href="#showcase" className="fw-btn fw-btn-secondary">
                  <i className="fas fa-play" style={{ marginRight: 8, fontSize: "0.85rem", color: "#C084FC" }} />
                  Watch Demo
                </a>
              </div>
            </div>

            <div className="fw-hero-image fw-scroll-animate">
              <div className="fw-hero-mockup-wrap">
                <img
                  src="https://i.ibb.co/Z1ksnwzz/Capture.png"
                  alt="Fuwalo Android Piano App Preview"
                  className="fw-hero-img"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== TRUST / PLATFORMS BAR ========== */}
      <div className="fw-trust-strip">
        <div className="fw-container fw-trust-inner">
          <div className="fw-trust-stat">
            <span className="fw-stat-num">Real-Time</span>
            <span className="fw-stat-desc">Low Latency Multiplayer</span>
          </div>
          <div className="fw-trust-divider" />
          <div className="fw-trust-stat">
            <span className="fw-stat-num">USB MIDI</span>
            <span className="fw-stat-desc">Plug &amp; Play Support</span>
          </div>
          <div className="fw-trust-divider" />
          <div className="fw-trust-stat">
            <span className="fw-stat-num">Interactive</span>
            <span className="fw-stat-desc">Lessons &amp; Rhythm Modes</span>
          </div>
          <div className="fw-trust-divider" />
          <div className="fw-trust-stat">
            <span className="fw-stat-num">100% Free</span>
            <span className="fw-stat-desc">On Google Play</span>
          </div>
        </div>
      </div>

      {/* ========== CORE FEATURES ========== */}
      <section id="features" className="fw-section fw-features-section">
        <div className="fw-container">
          <div className="fw-section-header fw-scroll-animate">
            <span className="fw-section-kicker">Everything You Need</span>
            <h2 className="fw-section-title">Built for Modern Piano Players</h2>
            <p className="fw-section-subtitle">
              Whether you are practicing your first scales, jamming with friends online, or connecting a 88-key digital piano.
            </p>
          </div>

          <div className="fw-features-grid">
            {/* Feature 1 */}
            <div className="fw-feature-card fw-scroll-animate">
              <div className="fw-feature-icon" style={{ background: "#F5F3FF", color: "#7C3AED" }}>
                <i className="fas fa-users" />
              </div>
              <h3 className="fw-feature-title">Real-Time Multiplayer</h3>
              <p className="fw-feature-desc">
                Create private rooms with friends or join public lobbies. Play duets, jam simultaneously, and hear every note in real time with synchronized low-latency sound.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="fw-feature-card fw-scroll-animate">
              <div className="fw-feature-icon" style={{ background: "#EFF6FF", color: "#2563EB" }}>
                <i className="fas fa-graduation-cap" />
              </div>
              <h3 className="fw-feature-title">Interactive Lessons &amp; Drills</h3>
              <p className="fw-feature-desc">
                Practice sight reading, chords, scales, and finger dexterity. Visual falling notes and instant feedback help you master songs step by step.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="fw-feature-card fw-scroll-animate">
              <div className="fw-feature-icon" style={{ background: "#FFFBEB", color: "#D97706" }}>
                <i className="fas fa-keyboard" />
              </div>
              <h3 className="fw-feature-title">USB MIDI Support</h3>
              <p className="fw-feature-desc">
                Plug in your digital piano or MIDI keyboard with a USB cable. Enjoy zero lag, touch-sensitive response, and full key range detection.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="fw-feature-card fw-scroll-animate">
              <div className="fw-feature-icon" style={{ background: "#FDF2F8", color: "#DB2777" }}>
                <i className="fas fa-gamepad" />
              </div>
              <h3 className="fw-feature-title">Falling MIDI Match Games</h3>
              <p className="fw-feature-desc">
                Turn song practice into an engaging rhythm game. Import your favorite MIDI files, hit note streaks, and climb competitive global leaderboards.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== APP SHOWCASE / VIDEO & SCREENSHOTS ========== */}
      <section id="showcase" className="fw-section fw-showcase-section">
        <div className="fw-container">
          <div className="fw-section-header fw-scroll-animate">
            <span className="fw-section-kicker">See Fuwalo in Action</span>
            <h2 className="fw-section-title">Designed for Clean, Fluid Gameplay</h2>
            <p className="fw-section-subtitle">
              Take a look at Fuwalo's clean interface, responsive virtual keyboard, and live multiplayer rooms.
            </p>
          </div>

          {/* Video Showcase */}
          <div className="fw-video-wrap fw-scroll-animate">
            <div className={`fw-video-container ${videoPlaying ? "fw-playing" : ""}`}>
              <video
                ref={videoRef}
                className="fw-video"
                playsInline
                controls
                preload="metadata"
                onPlay={() => setVideoPlaying(true)}
                onPause={() => setVideoPlaying(false)}
                onEnded={() => setVideoPlaying(false)}
              >
                <source src="/fuwalo-demo.mp4" type="video/mp4" />
              </video>
              {!videoPlaying && (
                <div
                  className="fw-video-overlay"
                  onClick={() => {
                    videoRef.current?.play();
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label="Play Fuwalo demo video"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") videoRef.current?.play();
                  }}
                >
                  <button className="fw-play-btn" aria-label="Play video" tabIndex={-1}>
                    <i className="fas fa-play" style={{ marginLeft: 4 }} />
                  </button>
                  <span className="fw-video-play-label">Watch 1-Minute Demo</span>
                </div>
              )}
            </div>
          </div>

          {/* Screenshots Gallery */}
          <div className="fw-screenshots-grid fw-scroll-animate">
            <div className="fw-screenshot-item">
              <img
                src="/screenshots/4c969790-ff11-432c-b7a7-728683f855f3.jfif"
                alt="Fuwalo piano keyboard display"
                className="fw-screenshot-img"
                loading="lazy"
              />
              <span className="fw-screenshot-caption">Virtual Keyboard &amp; Note Visualizer</span>
            </div>

            <div className="fw-screenshot-item">
              <img
                src="/screenshots/56bc0e99-e420-4a5a-8300-d113f7ba9362.jfif"
                alt="Fuwalo gameplay mode"
                className="fw-screenshot-img"
                loading="lazy"
              />
              <span className="fw-screenshot-caption">Rhythm Game &amp; Falling Notes</span>
            </div>

            <div className="fw-screenshot-item">
              <img
                src="/screenshots/b1b83425-2f48-49ec-a149-e28262f41fec.jfif"
                alt="Fuwalo song select menu"
                className="fw-screenshot-img"
                loading="lazy"
              />
              <span className="fw-screenshot-caption">Song Library &amp; Multiplayer Rooms</span>
            </div>
          </div>
        </div>
      </section>

      {/* ========== DOWNLOAD CTA SECTION ========== */}
      <section id="download" className="fw-section fw-download-cta-section">
        <div className="fw-container">
          <div className="fw-download-banner fw-scroll-animate">
            <div className="fw-download-banner-text">
              <h2 className="fw-download-banner-title">Ready to Start Playing?</h2>
              <p className="fw-download-banner-subtitle">
                Download Fuwalo today for free on Google Play. Join real-time rooms, practice with MIDI songs, and connect with piano learners worldwide.
              </p>
              <div className="fw-download-banner-buttons">
                <a
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fw-google-play-badge fw-google-play-badge-large"
                  aria-label="Get Fuwalo on Google Play"
                >
                  <i className="fab fa-google-play fw-play-icon" />
                  <div className="fw-play-text">
                    <span className="fw-play-sub">GET IT ON</span>
                    <span className="fw-play-main">Google Play</span>
                  </div>
                </a>

                <a
                  href={DISCORD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fw-btn fw-btn-discord"
                >
                  <i className="fab fa-discord" style={{ marginRight: 8 }} />
                  Join Discord
                </a>
              </div>
            </div>

            <div className="fw-download-qr-card">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(
                  PLAY_STORE_URL
                )}`}
                alt="QR Code to open Fuwalo on Google Play"
                width={140}
                height={140}
                className="fw-qr-image"
              />
              <span className="fw-qr-caption">Scan to install on Android</span>
            </div>
          </div>
        </div>
      </section>

      {/* ========== FAQ ========== */}
      <section id="faq" className="fw-section fw-faq-section">
        <div className="fw-container" style={{ maxWidth: 840 }}>
          <div className="fw-section-header fw-scroll-animate">
            <span className="fw-section-kicker">Frequently Asked Questions</span>
            <h2 className="fw-section-title">Got Questions? We've Got Answers.</h2>
          </div>

          <div className="fw-faq-list">
            {[
              {
                q: "Is Fuwalo free to download and play?",
                a: "Yes! Fuwalo is completely free to download from the Google Play Store with full access to multiplayer rooms, rhythm games, and practice modes.",
              },
              {
                q: "Do I need a physical piano or keyboard to use Fuwalo?",
                a: "Not at all. You can play directly on your Android phone or tablet using our responsive multi-touch virtual keyboard. If you do own a digital piano or MIDI keyboard, you can easily connect it via USB-OTG for ultra-low latency.",
              },
              {
                q: "How does the multiplayer piano feature work?",
                a: "You can host private rooms with a code to practice with friends or join open community lobbies. Every player's key presses are synchronized in real time with low latency so you can jam together smoothly.",
              },
              {
                q: "Can I import my own MIDI songs to practice?",
                a: "Yes! You can import any standard MIDI song into Fuwalo to practice at your own tempo, visualize falling notes, and play along in rhythm mode.",
              },
            ].map((item, i) => (
              <div
                className={`fw-faq-item ${openFaq === i ? "fw-faq-open" : ""}`}
                key={i}
              >
                <button
                  className="fw-faq-question"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                >
                  <span>{item.q}</span>
                  <i
                    className="fas fa-chevron-down"
                    style={{
                      transition: "transform 0.3s",
                      transform: openFaq === i ? "rotate(180deg)" : "rotate(0)",
                    }}
                  />
                </button>
                <div
                  className="fw-faq-answer"
                  style={{ maxHeight: openFaq === i ? 220 : 0 }}
                >
                  <div className="fw-faq-answer-inner">{item.a}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer className="fw-footer">
        <div className="fw-container">
          <div className="fw-footer-grid">
            <div className="fw-footer-brand">
              <div className="fw-footer-logo">
                <img
                  src={FUWALO_LOGO_PATH}
                  alt="Fuwalo Logo"
                  width={30}
                  height={30}
                  style={{ marginRight: 10, objectFit: "contain" }}
                />
                <span>Fuwalo</span>
              </div>
              <p className="fw-footer-tagline">
                Your modern multiplayer piano app — jam with friends in real time, connect MIDI keyboards, and master songs.
              </p>
              <div className="fw-footer-socials">
                <a
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Google Play Store"
                >
                  <i className="fab fa-google-play" />
                </a>
                <a
                  href={DISCORD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Discord Server"
                >
                  <i className="fab fa-discord" />
                </a>
                <a
                  href={TWITTER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Twitter / X"
                >
                  <i className="fab fa-twitter" />
                </a>
              </div>
            </div>

            <div className="fw-footer-col">
              <h4>Product</h4>
              <ul>
                <li>
                  <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
                    Download on Google Play
                  </a>
                </li>
                <li>
                  <a href="#features">Features</a>
                </li>
                <li>
                  <a href="#showcase">App Preview</a>
                </li>
              </ul>
            </div>

            <div className="fw-footer-col">
              <h4>Community</h4>
              <ul>
                <li>
                  <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer">
                    Discord Community
                  </a>
                </li>
                <li>
                  <a href={TWITTER_URL} target="_blank" rel="noopener noreferrer">
                    Twitter / X
                  </a>
                </li>
                <li>
                  <a href="/community.html">Community Hub</a>
                </li>
              </ul>
            </div>

            <div className="fw-footer-col">
              <h4>Legal &amp; Info</h4>
              <ul>
                <li>
                  <a href="/privacy.html">Privacy Policy</a>
                </li>
                <li>
                  <a href="/terms.html">Terms of Service</a>
                </li>
                <li>
                  <a href="/about.html">About</a>
                </li>
              </ul>
            </div>
          </div>

          <div className="fw-footer-bottom">
            <p>&copy; {new Date().getFullYear()} Fuwalo. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </>
  );
};

/* ================================================================== */
/*  Scoped styles — Clean, Modern, Professional Lavender & Slate Theme */
/* ================================================================== */
const fuwaloStyles = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap');

/* ---------- Global Reset & Fonts ---------- */
.fw-navbar,
.fw-hero,
.fw-section,
.fw-trust-strip,
.fw-footer {
  font-family: 'Plus Jakarta Sans', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  color: #0F172A;
  box-sizing: border-box;
}

/* ---------- Scroll progress ---------- */
.fw-scroll-progress {
  position: fixed; top: 0; left: 0; width: 0; height: 3px;
  background: linear-gradient(90deg, #7C3AED, #3B82F6, #EC4899);
  z-index: 1001; transition: width 0.1s ease;
}

/* ---------- Navbar ---------- */
.fw-navbar {
  position: sticky; top: 0; z-index: 100;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px);
  border-bottom: 1px solid rgba(226, 232, 240, 0.8);
  padding: 0.85rem 0;
  transition: background 0.3s;
}
.fw-nav-inner {
  max-width: 1240px; margin: 0 auto; padding: 0 1.5rem;
  display: flex; align-items: center; justify-content: space-between;
}
.fw-nav-brand {
  display: flex; align-items: center; text-decoration: none;
}
.fw-brand-text {
  font-size: 1.35rem; font-weight: 800; color: #1E1B4B; letter-spacing: -0.02em;
}
.fw-nav-links-desktop {
  display: none; align-items: center; gap: 0.5rem;
}
@media (min-width: 860px) { .fw-nav-links-desktop { display: flex; } }
.fw-nav-link {
  color: #475569; font-size: 0.92rem; font-weight: 600;
  padding: 0.5rem 0.85rem; border-radius: 8px; text-decoration: none;
  transition: all 0.2s ease; display: inline-flex; align-items: center;
}
.fw-nav-link:hover { color: #7C3AED; background: #F5F3FF; }

.fw-nav-action-desktop { display: none; }
@media (min-width: 860px) { .fw-nav-action-desktop { display: block; } }

.fw-btn-nav {
  background: #0F172A; color: #fff;
  padding: 0.6rem 1.15rem; font-size: 0.88rem; font-weight: 600;
  border-radius: 9999px; text-decoration: none; display: inline-flex; align-items: center;
  transition: all 0.2s ease; box-shadow: 0 2px 8px rgba(15, 23, 42, 0.15);
}
.fw-btn-nav:hover {
  background: #7C3AED; transform: translateY(-1px); box-shadow: 0 4px 14px rgba(124, 58, 237, 0.35);
}

.fw-mobile-toggle {
  display: flex; align-items: center; justify-content: center;
  width: 40px; height: 40px; background: none; border: none; color: #1E293B;
  font-size: 1.25rem; cursor: pointer; border-radius: 8px;
}
@media (min-width: 860px) { .fw-mobile-toggle { display: none; } }

.fw-mobile-menu {
  position: absolute; top: 100%; left: 0; width: 100%;
  background: #fff; border-bottom: 1px solid #E2E8F0;
  padding: 1.25rem 1.5rem; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  display: flex; flex-direction: column; gap: 0.5rem; z-index: 99;
}
.fw-mobile-menu a {
  display: flex; align-items: center; padding: 0.75rem 0.5rem;
  color: #334155; font-weight: 600; font-size: 1rem; text-decoration: none;
  border-bottom: 1px solid #F1F5F9;
}
.fw-mobile-play-btn {
  margin-top: 0.5rem; justify-content: center; width: 100%; padding: 0.85rem;
}

/* ---------- Hero Section ---------- */
.fw-hero {
  position: relative; padding: 4.5rem 0 4rem; overflow: hidden;
  background: linear-gradient(150deg, #4A3D6E 0%, #2A2146 100%);
  color: #fff;
}
@media (min-width: 900px) { .fw-hero { padding: 6.5rem 0 5.5rem; } }

.fw-hero-video {
  position: absolute; inset: 0; width: 100%; height: 100%;
  object-fit: cover; z-index: 0; opacity: 0.35;
}
.fw-hero-overlay {
  position: absolute; inset: 0;
  background: linear-gradient(135deg, rgba(74, 61, 110, 0.88) 0%, rgba(30, 24, 52, 0.94) 100%);
  z-index: 1;
}
.fw-hero-content {
  position: relative; z-index: 10; max-width: 1240px; margin: 0 auto; padding: 0 1.5rem;
}
.fw-hero-grid {
  display: flex; flex-direction: column; align-items: center; gap: 2.5rem;
}
@media (min-width: 900px) {
  .fw-hero-grid { flex-direction: row; align-items: center; justify-content: space-between; gap: 3.5rem; }
  .fw-hero-text { flex: 1.1; text-align: left; }
  .fw-hero-image { flex: 0.9; }
}

.fw-hero-badge {
  display: inline-flex; align-items: center; gap: 0.5rem;
  padding: 0.35rem 0.85rem; border-radius: 9999px;
  background: rgba(255, 255, 255, 0.12); backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-size: 0.8rem; font-weight: 600; color: #E9D5FF; margin-bottom: 1.25rem;
}
.fw-pulse-dot {
  width: 8px; height: 8px; border-radius: 50%; background: #4ADE80;
  box-shadow: 0 0 8px #4ADE80;
}

.fw-hero-title {
  font-size: 2.25rem; font-weight: 800; line-height: 1.15; margin: 0 0 1.25rem;
  letter-spacing: -0.03em;
}
@media (min-width: 640px) { .fw-hero-title { font-size: 3rem; } }
@media (min-width: 1024px) { .fw-hero-title { font-size: 3.6rem; } }

.fw-hero-title-accent {
  background: linear-gradient(90deg, #F3E8FF 0%, #C084FC 50%, #93C5FD 100%);
  -webkit-background-clip: text; background-clip: text; color: transparent;
}
.fw-hero-subtitle {
  font-size: 1.05rem; line-height: 1.65; color: #E2E8F0; margin: 0 0 1.75rem; max-width: 580px;
}
@media (min-width: 768px) { .fw-hero-subtitle { font-size: 1.15rem; } }

.fw-hero-highlights {
  display: flex; flex-wrap: wrap; gap: 1rem 1.5rem; margin-bottom: 2rem;
}
.fw-highlight-item {
  display: flex; align-items: center; gap: 0.5rem; font-size: 0.88rem; font-weight: 600; color: #F1F5F9;
}
.fw-highlight-item i { color: #A855F7; font-size: 0.95rem; }

.fw-hero-actions {
  display: flex; flex-wrap: wrap; gap: 1rem; align-items: center;
}

/* Official Google Play Badge Style */
.fw-google-play-badge {
  display: inline-flex; align-items: center; gap: 0.85rem;
  background: #000; color: #fff; text-decoration: none;
  padding: 0.65rem 1.35rem; border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s;
}
.fw-google-play-badge:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.45);
  border-color: rgba(255, 255, 255, 0.5);
}
.fw-google-play-badge-large {
  padding: 0.75rem 1.6rem;
}
.fw-play-icon { font-size: 1.75rem; color: #fff; }
.fw-play-text { display: flex; flex-direction: column; text-align: left; line-height: 1.15; }
.fw-play-sub { font-size: 0.62rem; letter-spacing: 0.08em; font-weight: 600; opacity: 0.85; }
.fw-play-main { font-size: 1.15rem; font-weight: 700; letter-spacing: -0.01em; }

.fw-btn-secondary {
  background: rgba(255, 255, 255, 0.12);
  color: #fff; border: 1px solid rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(8px);
  padding: 0.85rem 1.5rem; font-size: 0.95rem; font-weight: 700;
  border-radius: 12px; text-decoration: none; display: inline-flex; align-items: center;
  transition: all 0.2s ease;
}
.fw-btn-secondary:hover {
  background: rgba(255, 255, 255, 0.2); transform: translateY(-2px);
}

.fw-hero-mockup-wrap {
  position: relative; display: flex; justify-content: center;
}
.fw-hero-img {
  max-width: 100%; max-height: 440px; border-radius: 1.25rem;
  box-shadow: 0 25px 60px -15px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.15);
  animation: fwFloat 6s ease-in-out infinite;
}
@keyframes fwFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-12px); }
}

/* Floating note animations */
.fw-floating-note {
  position: absolute; color: rgba(255, 255, 255, 0.15);
  animation: fwNoteFloat 18s ease-in-out infinite; pointer-events: none; z-index: 5;
}
@keyframes fwNoteFloat {
  0% { transform: translateY(100vh) rotate(0deg); opacity: 0; }
  10% { opacity: 0.18; } 50% { opacity: 0.25; } 90% { opacity: 0.1; }
  100% { transform: translateY(-10vh) rotate(15deg); opacity: 0; }
}

/* ---------- Trust Strip ---------- */
.fw-trust-strip {
  background: #FAF8FD; border-bottom: 1px solid #E2E8F0; padding: 1.75rem 0;
}
.fw-trust-inner {
  display: flex; flex-wrap: wrap; align-items: center; justify-content: space-around; gap: 1.5rem;
}
.fw-trust-stat { text-align: center; }
.fw-stat-num {
  display: block; font-size: 1.2rem; font-weight: 800; color: #5B4B8A; margin-bottom: 0.2rem;
}
.fw-stat-desc { font-size: 0.8rem; font-weight: 600; color: #64748B; text-transform: uppercase; letter-spacing: 0.05em; }
.fw-trust-divider {
  display: none; width: 1px; height: 36px; background: #CBD5E1;
}
@media (min-width: 768px) { .fw-trust-divider { display: block; } }

/* ---------- Generic Section Layout ---------- */
.fw-section { padding: 4.5rem 0; }
@media (min-width: 768px) { .fw-section { padding: 6rem 0; } }
.fw-container { max-width: 1240px; margin: 0 auto; padding: 0 1.5rem; }

.fw-section-header { text-align: center; margin-bottom: 3.5rem; }
.fw-section-kicker {
  display: inline-block; font-size: 0.78rem; font-weight: 800; text-transform: uppercase;
  letter-spacing: 0.1em; color: #7C3AED; margin-bottom: 0.6rem;
}
.fw-section-title {
  font-size: 2rem; font-weight: 800; color: #0F172A; margin: 0 0 0.85rem; letter-spacing: -0.02em;
}
@media (min-width: 768px) { .fw-section-title { font-size: 2.5rem; } }
.fw-section-subtitle {
  font-size: 1.05rem; color: #475569; max-width: 620px; margin: 0 auto; line-height: 1.6;
}

/* ---------- Features Grid ---------- */
.fw-features-section { background: #fff; }
.fw-features-grid {
  display: grid; grid-template-columns: 1fr; gap: 1.5rem;
}
@media (min-width: 640px) { .fw-features-grid { grid-template-columns: repeat(2, 1fr); } }
@media (min-width: 1024px) { .fw-features-grid { grid-template-columns: repeat(4, 1fr); gap: 1.75rem; } }

.fw-feature-card {
  background: #FAF8FD; border: 1px solid rgba(226, 232, 240, 0.9);
  border-radius: 1.25rem; padding: 2rem 1.5rem;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s;
}
.fw-feature-card:hover {
  transform: translateY(-4px); box-shadow: 0 16px 32px -8px rgba(124, 58, 237, 0.1);
  border-color: #DDD6FE; background: #fff;
}
.fw-feature-icon {
  width: 52px; height: 52px; border-radius: 1rem;
  display: flex; align-items: center; justify-content: center; font-size: 1.35rem; margin-bottom: 1.25rem;
}
.fw-feature-title {
  font-size: 1.2rem; font-weight: 700; color: #0F172A; margin: 0 0 0.6rem;
}
.fw-feature-desc {
  font-size: 0.9rem; color: #475569; line-height: 1.6; margin: 0;
}

/* ---------- Showcase Section ---------- */
.fw-showcase-section { background: #FAF9FC; border-top: 1px solid #F1F5F9; border-bottom: 1px solid #F1F5F9; }
.fw-video-wrap { max-width: 900px; margin: 0 auto 3.5rem; }
.fw-video-container {
  position: relative; border-radius: 1.25rem; overflow: hidden;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.15); border: 1px solid #CBD5E1;
  background: #0F172A; aspect-ratio: 16/9;
}
.fw-video { width: 100%; height: 100%; object-fit: cover; display: block; }
.fw-video-overlay {
  position: absolute; inset: 0; background: rgba(15, 23, 42, 0.45);
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 1rem; cursor: pointer; backdrop-filter: blur(2px);
}
.fw-playing .fw-video-overlay { display: none; }
.fw-play-btn {
  width: 76px; height: 76px; border-radius: 50%; border: none;
  background: #fff; color: #7C3AED; font-size: 1.75rem;
  display: flex; align-items: center; justify-content: center; cursor: pointer;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3); transition: transform 0.2s ease;
}
.fw-play-btn:hover { transform: scale(1.1); }
.fw-video-play-label {
  color: #fff; font-weight: 700; font-size: 0.95rem; text-shadow: 0 2px 4px rgba(0,0,0,0.5);
}

.fw-screenshots-grid {
  display: grid; grid-template-columns: 1fr; gap: 2rem;
}
@media (min-width: 640px) { .fw-screenshots-grid { grid-template-columns: repeat(3, 1fr); } }
.fw-screenshot-item {
  display: flex; flex-direction: column; align-items: center; text-align: center;
}
.fw-screenshot-img {
  width: 100%; max-width: 240px; border-radius: 1.5rem;
  box-shadow: 0 16px 36px -10px rgba(15, 23, 42, 0.2);
  border: 4px solid #fff; margin-bottom: 1rem;
  transition: transform 0.3s ease;
}
.fw-screenshot-item:hover .fw-screenshot-img { transform: translateY(-6px); }
.fw-screenshot-caption {
  font-size: 0.88rem; font-weight: 700; color: #334155;
}

/* ---------- Download CTA Banner ---------- */
.fw-download-cta-section {
  background: linear-gradient(135deg, #5B4B8A 0%, #3B2E5C 100%);
  color: #fff;
}
.fw-download-banner {
  display: flex; flex-direction: column; align-items: center; justify-content: space-between;
  gap: 2.5rem; text-align: center;
}
@media (min-width: 860px) {
  .fw-download-banner { flex-direction: row; text-align: left; }
  .fw-download-banner-text { max-width: 600px; }
}
.fw-download-banner-title {
  font-size: 2.2rem; font-weight: 800; margin: 0 0 1rem; letter-spacing: -0.02em;
}
@media (min-width: 768px) { .fw-download-banner-title { font-size: 2.6rem; } }
.fw-download-banner-subtitle {
  font-size: 1.05rem; color: #E2E8F0; line-height: 1.65; margin: 0 0 2rem;
}
.fw-download-banner-buttons {
  display: flex; flex-wrap: wrap; gap: 1rem; align-items: center; justify-content: center;
}
@media (min-width: 860px) { .fw-download-banner-buttons { justify-content: flex-start; } }

.fw-btn-discord {
  background: #5865F2; color: #fff; padding: 0.85rem 1.5rem;
  font-size: 0.95rem; font-weight: 700; border-radius: 12px; text-decoration: none;
  display: inline-flex; align-items: center; box-shadow: 0 4px 14px rgba(88, 101, 242, 0.4);
  transition: all 0.2s ease;
}
.fw-btn-discord:hover {
  background: #4752C4; transform: translateY(-2px); box-shadow: 0 8px 24px rgba(88, 101, 242, 0.5);
}

.fw-download-qr-card {
  background: #fff; border-radius: 1.25rem; padding: 1.5rem;
  display: flex; flex-direction: column; align-items: center;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.25);
}
.fw-qr-image {
  border-radius: 0.5rem; display: block;
}
.fw-qr-caption {
  font-size: 0.78rem; font-weight: 700; color: #475569; margin-top: 0.75rem; text-transform: uppercase; letter-spacing: 0.04em;
}

/* ---------- FAQ ---------- */
.fw-faq-section { background: #FAF9FC; }
.fw-faq-list { display: flex; flex-direction: column; gap: 0.85rem; }
.fw-faq-item {
  background: #fff; border: 1px solid #E2E8F0; border-radius: 1rem;
  overflow: hidden; transition: border-color 0.2s;
}
.fw-faq-item.fw-faq-open { border-color: #C4B5FD; }
.fw-faq-question {
  width: 100%; text-align: left; padding: 1.25rem 1.5rem;
  display: flex; align-items: center; justify-content: space-between;
  background: none; border: none; cursor: pointer;
  font-size: 1.05rem; font-weight: 700; color: #0F172A; font-family: inherit;
}
.fw-faq-question:hover { background: #FAF8FD; }
.fw-faq-question i { color: #7C3AED; font-size: 0.9rem; }
.fw-faq-answer { max-height: 0; overflow: hidden; transition: max-height 0.3s ease; }
.fw-faq-answer-inner {
  padding: 0 1.5rem 1.25rem; font-size: 0.95rem; color: #475569; line-height: 1.65;
}

/* ---------- Footer ---------- */
.fw-footer {
  background: #181528; color: #fff; padding: 4.5rem 0 2rem; border-top: 1px solid rgba(255,255,255,0.06);
}
.fw-footer-grid {
  display: grid; grid-template-columns: 1fr; gap: 2.5rem; margin-bottom: 3.5rem;
}
@media (min-width: 768px) {
  .fw-footer-grid { grid-template-columns: 2fr repeat(3, 1fr); gap: 2rem; }
}
.fw-footer-logo {
  display: flex; align-items: center; font-size: 1.35rem; font-weight: 800; margin-bottom: 0.85rem;
}
.fw-footer-tagline {
  color: #94A3B8; font-size: 0.9rem; line-height: 1.6; margin: 0 0 1.25rem; max-width: 320px;
}
.fw-footer-socials { display: flex; gap: 0.85rem; }
.fw-footer-socials a {
  width: 38px; height: 38px; border-radius: 50%; background: rgba(255, 255, 255, 0.08);
  display: flex; align-items: center; justify-content: center;
  color: #CBD5E1; font-size: 1rem; text-decoration: none; transition: all 0.2s ease;
}
.fw-footer-socials a:hover {
  background: #7C3AED; color: #fff; transform: translateY(-2px);
}
.fw-footer-col h4 {
  font-size: 0.9rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em;
  color: #F1F5F9; margin: 0 0 1rem;
}
.fw-footer-col ul { list-style: none; padding: 0; margin: 0; }
.fw-footer-col ul li { margin-bottom: 0.65rem; }
.fw-footer-col ul a {
  color: #94A3B8; font-size: 0.88rem; text-decoration: none; transition: color 0.2s;
}
.fw-footer-col ul a:hover { color: #fff; }
.fw-footer-bottom {
  border-top: 1px solid rgba(255, 255, 255, 0.08); padding-top: 2rem;
  text-align: center; color: #64748B; font-size: 0.85rem;
}

/* ---------- Scroll Animations ---------- */
.fw-scroll-animate {
  opacity: 0; transform: translateY(24px); transition: opacity 0.6s ease, transform 0.6s ease;
}
.fw-scroll-animate.fw-animate {
  opacity: 1; transform: translateY(0);
}
`;
